// Service principal de cache des médias
import { MediaCacheDB, type CachedMedia, type CacheStats } from './MediaCacheDB';
import type { MediaCacheConfig } from '../Config/config';

export interface MediaCacheContext {
    instanceId: string;
    componentId: string;
}

class MediaCacheServiceClass {
    private config: MediaCacheConfig | null = null;
    private isInitialized = false;
    private fetchInterceptor: ((url: string) => Promise<Response>) | null = null;

    /**
     * Initialise le service de cache
     */
    async init(config: MediaCacheConfig): Promise<void> {
        this.config = config;
        
        if (!config.enabled) {
            this.log('⚠️ Cache désactivé dans la configuration');
            return;
        }

        try {
            await MediaCacheDB.init(config);
            this.setupFetchInterceptor();
            this.isInitialized = true;
            this.log('✅ MediaCacheService initialisé');
        } catch (error) {
            this.log('❌ Erreur initialisation cache:', error);
            throw error;
        }
    }

    /**
     * Configure l'intercepteur fetch pour sauvegarder automatiquement les médias
     */
    private setupFetchInterceptor(): void {
        if (!this.config?.enabled) return;

        // Sauvegarder la fonction fetch originale
        const originalFetch = window.fetch;

        // Intercepter tous les appels fetch
        window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const url = typeof input === 'string' ? input : input.toString();
            
            // Appeler fetch original
            const response = await originalFetch(input, init);
            
            // Intercepter les réponses de médias
            if (this.isMediaResponse(url, response)) {
                this.handleMediaResponse(url, response).catch(error => {
                    this.log('❌ Erreur traitement réponse média:', error);
                });
            }
            
            return response;
        };

        this.log('🔄 Intercepteur fetch configuré');
    }

    /**
     * Vérifie si une réponse est un média à cacher
     */
    private isMediaResponse(url: string, response: Response): boolean {
        if (!this.config?.enabled) return false;
        
        // Vérifier si c'est une URL d'API de médias
        const isMediaApi = url.includes('/api/v1/upload/public/serve/') || 
                          url.includes('/api/v1/upload/public/hls/');
        
        // Vérifier le Content-Type
        const contentType = response.headers.get('content-type') || '';
        const isMediaType = contentType.startsWith('image/') || 
                           contentType.startsWith('video/') ||
                           contentType.includes('application/vnd.apple.mpegurl') ||
                           contentType.includes('video/mp2t');

        return isMediaApi && isMediaType && response.ok;
    }

    /**
     * Traite une réponse média pour la sauvegarder dans le cache
     */
    private async handleMediaResponse(url: string, response: Response): Promise<void> {
        try {
            // Cloner la réponse pour éviter de consommer le stream
            const clonedResponse = response.clone();
            const blob = await clonedResponse.blob();
            
            // Extraire le file_id de l'URL
            const fileId = this.extractFileIdFromUrl(url);
            if (!fileId) {
                this.log('⚠️ Impossible d\'extraire file_id de:', url);
                return;
            }

            // Déterminer le type de média
            const contentType = response.headers.get('content-type') || '';
            const mediaType = this.determineMediaType(contentType, url);

            // Sauvegarder dans le cache avec un contexte temporaire
            // Le contexte réel sera fourni par le composant qui fait la requête
            await this.saveMediaToCache(fileId, blob, contentType, 'temp', 'temp');
            
        } catch (error) {
            this.log('❌ Erreur sauvegarde automatique:', error);
        }
    }

    /**
     * Extrait le file_id d'une URL d'API
     */
    private extractFileIdFromUrl(url: string): string | null {
        // Patterns d'URLs API:
        // /api/v1/upload/public/serve/image/{file_id}
        // /api/v1/upload/public/serve/video/{file_id}
        // /api/v1/upload/public/hls/{file_id}/master.m3u8
        // /api/v1/upload/public/hls/{file_id}/segment_000.ts

        const patterns = [
            /\/api\/v1\/upload\/public\/serve\/(?:image|video|file)\/([^/?]+)/,
            /\/api\/v1\/upload\/public\/hls\/([^/?]+)\//
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                return match[1];
            }
        }

        return null;
    }

    /**
     * Détermine le type de média basé sur le Content-Type et l'URL
     */
    private determineMediaType(contentType: string, url: string): string {
        if (contentType.startsWith('image/')) return 'image';
        if (contentType.startsWith('video/')) return 'video';
        if (contentType.includes('application/vnd.apple.mpegurl')) return 'hls-manifest';
        if (contentType.includes('video/mp2t')) return 'hls-segment';
        if (url.includes('.m3u8')) return 'hls-manifest';
        if (url.includes('.ts')) return 'hls-segment';
        return 'unknown';
    }

    /**
     * Vérifie si un média est en cache et retourne l'URL blob si disponible
     */
    async getCachedMediaUrl(fileId: string, context: MediaCacheContext): Promise<string | null> {
        if (!this.isInitialized || !this.config?.enabled) {
            return null;
        }

        try {
            const cachedMedia = await MediaCacheDB.getMedia(fileId);
            if (cachedMedia) {
                // Mettre à jour les instances utilisant ce média
                await this.updateMediaInstances(fileId, context);
                return URL.createObjectURL(cachedMedia.blob);
            }
            return null;
        } catch (error) {
            this.log('❌ Erreur récupération cache:', error);
            return null;
        }
    }

    /**
     * Sauvegarde un média dans le cache avec son contexte
     */
    async saveMediaToCache(
        fileId: string, 
        blob: Blob, 
        contentType: string, 
        instanceId: string, 
        componentId: string
    ): Promise<void> {
        if (!this.isInitialized || !this.config?.enabled) {
            return;
        }

        try {
            await MediaCacheDB.saveMedia(fileId, blob, contentType, instanceId, componentId);
        } catch (error) {
            this.log('❌ Erreur sauvegarde cache:', error);
            throw error;
        }
    }

    /**
     * Met à jour les instances utilisant un média
     */
    private async updateMediaInstances(fileId: string, context: MediaCacheContext): Promise<void> {
        try {
            // Récupérer les instances actuelles
            const cachedMedia = await MediaCacheDB.getMedia(fileId);
            if (cachedMedia) {
                const instances = [...new Set([...cachedMedia.instances, context.instanceId])];
                await MediaCacheDB.updateMediaInstances(fileId, instances);
            }
        } catch (error) {
            this.log('❌ Erreur mise à jour instances:', error);
        }
    }

    /**
     * Supprime un média du cache
     */
    async deleteMedia(fileId: string): Promise<void> {
        if (!this.isInitialized || !this.config?.enabled) {
            return;
        }

        try {
            await MediaCacheDB.deleteMedia(fileId);
        } catch (error) {
            this.log('❌ Erreur suppression cache:', error);
            throw error;
        }
    }

    /**
     * Récupère les statistiques du cache
     */
    async getCacheStats(): Promise<CacheStats> {
        if (!this.isInitialized || !this.config?.enabled) {
            return { totalFiles: 0, totalSize: 0, oldestFile: null, newestFile: null };
        }

        try {
            return await MediaCacheDB.getStats();
        } catch (error) {
            this.log('❌ Erreur récupération stats:', error);
            return { totalFiles: 0, totalSize: 0, oldestFile: null, newestFile: null };
        }
    }

    /**
     * Nettoie les médias orphelins
     */
    async cleanupOrphanedMedia(): Promise<number> {
        if (!this.isInitialized || !this.config?.enabled) {
            return 0;
        }

        try {
            return await MediaCacheDB.cleanupOrphanedMedia();
        } catch (error) {
            this.log('❌ Erreur nettoyage:', error);
            return 0;
        }
    }

    /**
     * Vérifie si le cache est activé
     */
    isEnabled(): boolean {
        return this.isInitialized && this.config?.enabled === true;
    }

    /**
     * Vérifie si les logs sont activés
     */
    isLoggingEnabled(): boolean {
        return this.config?.enableLogs === true;
    }

    /**
     * Ferme le service de cache
     */
    close(): void {
        MediaCacheDB.close();
        this.isInitialized = false;
        this.log('🔒 MediaCacheService fermé');
    }

    /**
     * Log conditionnel selon la configuration
     */
    private log(message: string, ...args: any[]): void {
        if (this.config?.enableLogs) {
            console.log(`[MediaCacheService] ${message}`, ...args);
        }
    }
}

// Export singleton
export const MediaCacheService = new MediaCacheServiceClass();

